import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer } from "./element-renderer"
import { cn } from "../lib/utils"

/** Internal route: starts with / but not // (protocol-relative) and no external domain. */
function isInternalRoute(href: string): boolean {
  return href.startsWith("/") && !href.startsWith("//") && !href.startsWith("/http")
}

export function LinkRenderer({ element }: RendererProps) {
  const { resolveSymbol, navigate } = useRenderContext()
  const atomDefaults = useAtomDefaults("link")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const linkDataAttrs = {
    "data-atom-type": "link" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  }
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined
  const href = element.href ? String(resolveSymbol(String(element.href)) ?? "") : "#"
  const target = element.target as string | undefined
  const rel = element.rel as string | undefined ?? (target === "_blank" ? "noopener noreferrer" : undefined)

  const children = element.children?.map((child) => (
    <ElementRenderer key={child.id} element={child} />
  ))

  // Internal links: use app router navigate (respects app prefix)
  if (navigate && isInternalRoute(href) && target !== "_blank") {
    return (
      <a
        href={href}
        onClick={(e) => {
          e.preventDefault()
          navigate(href)
        }}
        className={cn(element.className)}
        data-action="navigate"
        {...linkDataAttrs}
      >
        {children ?? text}
      </a>
    )
  }

  return (
    <a
      href={href}
      target={target}
      rel={rel}
      className={cn(element.className)}
      data-action="navigate"
      {...linkDataAttrs}
    >
      {children ?? text}
    </a>
  )
}
